import {
  faAlignJustify,
  faCalendar,
  faCheck,
  faGripVertical
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AvatarTask from 'components/common/Avatar';
import Flex from 'components/common/Flex';
import SingleSelect from 'components/common/SingleSelect';
import DeadlineTaskEditModal from 'components/modals/DeadlineTaskEditModal';
import DialogModal from 'components/modals/DialogModal';
import TicketTaskEditModal from 'components/modals/TicketTaskEditModal';
import useApi from 'helpers/api';
import { formatDeadlineSpecificTime } from 'helpers/utils';
import { useAuthenticatedUser } from 'hooks/useAuthenticatedUser';
import useNotification from 'hooks/useNotification';
import { useEffect, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import {
  Button,
  Card,
  Dropdown,
  Form,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import AgentPhotoService from 'services/AgentPhoto.service';
import TicketTasksService from 'services/TicketTasks.service';
import './TaskCard.css';

const TaskCard = ({
  ticketId,
  tasks,
  refresh,
  ticketIsCompleted,
  refreshLogs
}) => {
  const api = useApi();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const { authenticatedUser } = useAuthenticatedUser();

  const selectAllCheckboxRef = useRef(null);
  // Refs para elementos de edição
  const titleInputRef = useRef(null);
  const deadlineInputRef = useRef(null);
  const agentSelectRef = useRef(null);
  const editingCardRef = useRef(null);

  const [agents, setAgents] = useState([]);
  const [formattedAgents, setFormattedAgents] = useState([]);
  const [agentPhotos, setAgentPhotos] = useState({});
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(true);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [localTaskStatus, setLocalTaskStatus] = useState({});
  const [taskIdToDelete, setTaskIdToDelete] = useState(null);
  const [task, setTask] = useState({});
  // Estado para armazenar a tarefa a ser editada quando o usuário clicar no botão para editar o prazo
  const [taskToDeadlineEdit, setTaskDeadlineEdit] = useState({});
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [taskItems, setTaskItems] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);

  // Estado para controle de edição por campo
  const [editingField, setEditingField] = useState({
    taskId: null,
    field: null // 'title', 'deadline', 'agent'
  });

  // Estado para armazenar os dados durante a edição
  const [editData, setEditData] = useState({});

  // Estado para controlar a visualização de modal
  const [showEditDeadlineModal, setShowEditDeadlineModal] = useState(false);
  const [showEditDeadlineBulkActionModal, setShowEditDeadlineBulkActionModal] =
    useState(false);

  // Set default values for new task
  const getTodayDatetime = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set time to 00:00:00.000
    return new Date(today.getTime() - today.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16); // Format as YYYY-MM-DDT00:00 in local timezone
  };

  const [bulkBody, setBulkBody] = useState({
    operation: '',
    deadline: getTodayDatetime(),
    agentId: null
  });

  const [selectedBulkAction, setSelectedBulkAction] = useState(null);
  const [selectedAgentBulkAction, setSelectedAgentBulkAction] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);

  const optionsBulkActions = [
    { value: '', label: 'Ações em massa' },
    { value: 'COMPLETE', label: 'Marcar como concluído' },
    { value: 'UNCOMPLETE', label: 'Marcar como não concluído' },
    { value: 'SET_DEADLINE', label: 'Definir prazo' },
    { value: 'SET_AGENT', label: 'Definir responsável' },
    { value: 'DELETE', label: 'Excluir' }
  ];

  useEffect(() => {
    getAgents();
  }, []);

  useEffect(() => {
    if (authenticatedUser?.id) {
      setCurrentUserId(authenticatedUser.id);
    }
  }, [authenticatedUser]);

  // Quando as tarefas carregarem, inicialize o estado local
  useEffect(() => {
    if (tasks) {
      const statusMap = {};
      tasks.forEach(task => {
        statusMap[task.id] = task.status;
      });
      setLocalTaskStatus(statusMap);

      // Atualiza o estado de taskItems para o react-beautiful-dnd
      setTaskItems(tasks);
    }
  }, [tasks]);

  useEffect(() => {
    fetchAgentPhotos();
  }, [tasks]);

  // Efeito para gerenciar cliques fora dos campos em edição
  useEffect(() => {
    const handleClickOutside = event => {
      // Se estamos editando e o clique foi fora do card ou em outro card
      if (
        editingField.taskId &&
        editingCardRef.current &&
        !editingCardRef.current.contains(event.target)
      ) {
        saveChanges(editingField.taskId);
      }
    };

    if (editingField.taskId !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingField]);

  // Efeito para focar no input quando começar a editar
  useEffect(() => {
    if (editingField.field === 'title' && titleInputRef.current) {
      titleInputRef.current.focus();
    } else if (editingField.field === 'deadline' && deadlineInputRef.current) {
      deadlineInputRef.current.focus();
      deadlineInputRef.current.click();
    } else if (editingField.field === 'agent' && agentSelectRef.current) {
      agentSelectRef.current.focus();
    }
  }, [editingField]);

  useEffect(() => {
    if (selectAllCheckboxRef.current && tasks?.length > 0) {
      // Checkbox indeterminado se algumas (mas não todas) tasks estão selecionadas
      const isIndeterminate =
        selectedTasks.length > 0 && selectedTasks.length < tasks.length;

      // Checkbox marcado se todas as tasks estão selecionadas
      const isChecked =
        tasks.length > 0 && selectedTasks.length === tasks.length;

      selectAllCheckboxRef.current.indeterminate = isIndeterminate;
      selectAllCheckboxRef.current.checked = isChecked;
    }
  }, [selectedTasks, tasks]);

  useEffect(() => {
    if (selectedBulkAction)
      setBulkBody({ ...bulkBody, operation: selectedBulkAction.value });
    else setBulkBody({ ...bulkBody, operation: '' });

    if (selectedBulkAction && selectedBulkAction.value === 'SET_DEADLINE')
      setShowEditDeadlineBulkActionModal(true);
  }, [selectedBulkAction]);

  useEffect(() => {
    const edit = async () => {
      if (!taskToDeadlineEdit) return;

      const response = await TicketTasksService.update(
        ticketId,
        taskToDeadlineEdit.id,
        taskToDeadlineEdit
      );
      if (response.id) refresh();
    };

    edit();
  }, [taskToDeadlineEdit]);

  useEffect(() => {
    if (!selectedAgentBulkAction) return;

    setBulkBody({ ...bulkBody, agentId: selectedAgentBulkAction.value });
  }, [selectedAgentBulkAction]);

  const fetchAgentPhotos = async () => {
    if (!tasks?.content?.length) {
      setIsLoadingPhotos(false);
      return;
    }

    try {
      const uniqueAgentIds = extractUniqueAgentIds(tasks);
      const photosMap = {};

      await Promise.all(
        uniqueAgentIds.map(async agentId => {
          try {
            const request = await AgentPhotoService.find(agentId);
            if (request.ok) {
              const response = await request.json();
              photosMap[agentId] = response.url;
            }
          } catch (error) {
            console.log(`Foto não encontrada para o agente ${agentId}`);
          }
        })
      );

      setAgentPhotos(photosMap);
    } catch (error) {
      console.error('Erro ao buscar fotos dos agentes:', error);
    } finally {
      setIsLoadingPhotos(false);
    }
  };

  const extractUniqueAgentIds = tasksData => {
    const agentIds = new Set();

    tasksData.content.forEach(task => {
      if (task.agent && task.agent.id) {
        agentIds.add(task.agent.id);
      }
    });

    return [...agentIds];
  };

  const getAgentPhoto = agentId => {
    return agentId && agentPhotos[agentId] ? agentPhotos[agentId] : null;
  };

  const getAgents = async () => {
    const response = await api.agents.listSummaryData();
    setAgents(response);

    const optionsAgents = response.map(item => ({
      value: item.id,
      label: `${item.firstName} ${item.lastName}`
    }));

    optionsAgents.unshift({ value: null, label: 'Sem responsável' });

    setFormattedAgents(optionsAgents);
  };

  // Função auxiliar para determinar a cor da deadline
  const getDeadlineColor = deadlineDate => {
    if (!deadlineDate) return 'text-secondary';

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const deadline = new Date(deadlineDate);
    deadline.setHours(0, 0, 0, 0);

    if (deadline < today) {
      return 'text-danger';
    } else if (deadline.getTime() === today.getTime()) {
      return 'text-success';
    } else {
      return 'text-info';
    }
  };
  // Iniciar edição de um campo específico
  const startEditing = (task, field) => {
    // Se já estamos editando algo, salve as alterações anteriores
    if (editingField.taskId !== null) {
      saveChanges(editingField.taskId);
    }

    setEditingField({
      taskId: task.id,
      field: field
    });

    // Se estiver iniciando a edição do campo de agente, configura o agente selecionado
    if (field === 'agent' && task.agent) {
      const selectedOption = formattedAgents.find(
        option => option.value === task.agent.id
      );
      setSelectedAgent(
        selectedOption || { value: null, label: 'Sem responsável' }
      );
    }

    // Inicializa os dados de edição com os valores atuais da tarefa
    setEditData(prevData => ({
      ...prevData,
      [task.id]: {
        title: task.title,
        deadline: task.deadline
          ? task.deadline.substr(0, task.deadline.lastIndexOf(':'))
          : '',
        agent: { id: task.agent?.id || '' },
        isGroupTitle: task.isGroupTitle,
        status: task.status,
        description: task.description
      }
    }));
  };
  // Cancelar edição
  const cancelEditing = () => {
    setEditingField({ taskId: null, field: null });
    setSelectedAgent(null); // Limpa o agente selecionado
  };

  // Salvar alterações
  const saveChanges = taskId => {
    if (!editData[taskId]) return;

    handleUpdateTask(taskId, editData[taskId]);
    setEditingField({ taskId: null, field: null });
    setSelectedAgent(null); // Limpa o agente selecionado
  };

  // Atualizar tarefa na API
  const handleUpdateTask = async (taskId, updatedData) => {
    try {
      await TicketTasksService.update(ticketId, taskId, updatedData);
      refresh();
    } catch (error) {
      console.error('Erro ao atualizar tarefa:', error);
    }
  };

  // Atualiza o valor sendo editado
  const handleInputChange = (taskId, field, value) => {
    setEditData(prevData => ({
      ...prevData,
      [taskId]: {
        ...prevData[taskId],
        [field]: value
      }
    }));
  };

  const handleStatusChange = async (taskId, currentStatus) => {
    try {
      // Atualiza o estado local imediatamente para feedback visual
      setLocalTaskStatus(prev => ({
        ...prev,
        [taskId]: !currentStatus
      }));

      // Chama o serviço apropriado baseado no status atual
      if (currentStatus) {
        await TicketTasksService.uncomplete(ticketId, taskId);
      } else {
        await TicketTasksService.complete(ticketId, taskId);
      }

      if (!currentStatus) refreshLogs(); // Atualiza os logs se a tarefa foi concluída

      refresh();
    } catch (error) {
      console.error('Erro ao alterar status da tarefa:', error);
      // Reverte o estado local em caso de erro
      setLocalTaskStatus(prev => ({
        ...prev,
        [taskId]: currentStatus
      }));
    }
  };

  // Quando o Enter é pressionado, salvar as alterações
  const handleKeyDown = (e, taskId) => {
    if (e.key === 'Enter') {
      saveChanges(taskId);
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  const handleDeleteTask = async response => {
    if (response) {
      const request = await TicketTasksService.trash(ticketId, taskIdToDelete);

      if (request.ok) {
        showSuccessNotification('Tarefa excluída com sucesso');
        refresh();
        refreshLogs();
      } else {
        showErrorNotification(request.userMessage);
      }

      setTaskIdToDelete(null);
      setShowDeleteConfirmModal(false);
    }
  };

  const selectAllTasks = () => {
    const allSelected = selectedTasks.length === tasks.length;
    const newSelectedTasks = allSelected ? [] : tasks.map(task => task.id);

    setSelectedTasks(newSelectedTasks);
  };

  const handleSelectTask = e => {
    const taskId = parseInt(e.target.value);
    const isChecked = e.target.checked;

    if (isChecked) {
      setSelectedTasks(prev => [...prev, taskId]);
    } else {
      setSelectedTasks(prev => prev.filter(id => id !== taskId));
    }
  };

  const applyBulkAction = async () => {
    const { operation, deadline, agentId } = bulkBody;
    const bulkData = {
      taskIds: selectedTasks,
      operation,
      deadline: operation === 'SET_DEADLINE' ? deadline : null,
      agentId: operation === 'SET_AGENT' ? agentId : null
    };

    try {
      const response = await TicketTasksService.bulk(ticketId, bulkData);

      if (response.ok) {
        showSuccessNotification('Ação em massa aplicada com sucesso');
        refresh();
        refreshLogs();
        setSelectedBulkAction(optionsBulkActions[0]);
      } else {
        showErrorNotification(response.userMessage);
      }
    } catch (error) {
      console.error('Erro ao aplicar ação em massa:', error);
      showErrorNotification('Erro ao aplicar ação em massa');
    } finally {
      setSelectedTasks([]);
      setBulkBody({
        taskIds: [],
        operation: '',
        deadline: getTodayDatetime(),
        agentId: null
      });
    }
  };

  // Nova função para atualizar a ordem das tarefas
  const orderTask = async (taskId, taskAboveId, taskBelowId) => {
    const payload = {
      taskAboveId: taskAboveId === null ? null : taskAboveId,
      taskBelowId: taskBelowId === null ? null : taskBelowId
    };

    const response = await TicketTasksService.order(ticketId, taskId, payload);

    if (!response.ok) {
      const errorData = await response.json();
      showErrorNotification(
        errorData.userMessage || 'Erro ao atualizar a ordem das tarefas'
      );
      // Recarrega as tarefas para voltar à ordem original em caso de erro
      refresh();
      return false;
    }

    return true;
  };

  // Manipulador de eventos para o final do drag and drop
  const handleDragEnd = async result => {
    const { destination, source, draggableId } = result;

    // Se não houver destino ou o destino for o mesmo que a origem, não fazer nada
    if (!destination || destination.index === source.index) {
      return;
    }

    const taskId = parseInt(draggableId);

    // Cria uma cópia do array de tarefas
    const newTaskItems = Array.from(taskItems);
    // Remove a tarefa da posição antiga
    const [movedTask] = newTaskItems.splice(source.index, 1);
    // Insere a tarefa na nova posição
    newTaskItems.splice(destination.index, 0, movedTask);

    // Atualiza o estado para dar feedback visual imediato
    setTaskItems(newTaskItems);

    // Determina as tarefas acima e abaixo
    let taskAboveId = null;
    let taskBelowId = null;

    if (destination.index === 0) {
      // Se for movido para o topo
      taskAboveId = null;
      taskBelowId = newTaskItems[1]?.id || null;
    } else if (destination.index === newTaskItems.length - 1) {
      // Se for movido para o final
      taskAboveId = newTaskItems[destination.index - 1]?.id || null;
      taskBelowId = null;
    } else {
      // Se for movido para o meio
      taskAboveId = newTaskItems[destination.index - 1]?.id || null;
      taskBelowId = newTaskItems[destination.index + 1]?.id || null;
    }

    // Chama a API para persistir a mudança
    const success = await orderTask(taskId, taskAboveId, taskBelowId);

    if (success) {
      // Se a atualização foi bem-sucedida, recarregar as tarefas
      // para garantir que estamos exibindo a ordem correta do servidor
      refresh();
    }
  };

  const convertToTask = async taskData => {
    const updatedTask = {
      isGroupTitle: false,
      status: false,
      agent: { id: currentUserId || null },
      deadline: getTodayDatetime(),
      title: taskData.title
    };
    const response = await TicketTasksService.update(
      ticketId,
      taskData.id,
      updatedTask
    );

    if (response.id) {
      showSuccessNotification('Título convertido em tarefa com sucesso');
      refresh();
    } else {
      showErrorNotification(response.userMessage);
    }
  };

  const convertToTitle = async taskData => {
    const updatedTask = {
      isGroupTitle: true,
      status: false,
      agent: { id: currentUserId || null },
      deadline: getTodayDatetime(),
      title: taskData.title
    };
    const response = await TicketTasksService.update(
      ticketId,
      taskData.id,
      updatedTask
    );
    if (response.id) {
      showSuccessNotification('Tarefa convertida em título com sucesso');
      refresh();
    } else {
      showErrorNotification(response.userMessage);
    }
  };
  const handleChangeAgent = async (e, task) => {
    const { value, label } = e;

    // Atualiza o agente selecionado para exibição no select
    setSelectedAgent({ value, label });

    const updatedEditData = {
      title: task.title,
      deadline: task.deadline
        ? task.deadline.substr(0, task.deadline.lastIndexOf(':'))
        : '',
      agent: { id: value || null }, // Atualiza com o novo agente
      isGroupTitle: task.isGroupTitle,
      status: task.status,
      description: task.description
    };

    // Atualiza o estado editData
    setEditData(prevData => ({
      ...prevData,
      [task.id]: updatedEditData
    }));

    try {
      await handleUpdateTask(task.id, updatedEditData);

      setEditingField({ taskId: null, field: null });
      setSelectedAgent(null);
    } catch (error) {
      console.error('Erro ao atualizar agente:', error);
      showErrorNotification('Erro ao atualizar o agente da tarefa');

      // Em caso de erro, volta ao estado anterior
      setSelectedAgent(
        task.agent
          ? {
              value: task.agent.id,
              label: `${task.agent.firstName} ${task.agent.lastName}`
            }
          : { value: null, label: 'Sem responsável' }
      );

      setEditData(prevData => ({
        ...prevData,
        [task.id]: {
          title: task.title,
          deadline: task.deadline
            ? task.deadline.substr(0, task.deadline.lastIndexOf(':'))
            : '',
          agent: { id: task.agent?.id || null },
          isGroupTitle: task.isGroupTitle,
          status: task.status,
          description: task.description
        }
      }));
    }
  };

  const isBulkActionDisabled =
    selectedTasks.length === 0 || !bulkBody.operation || ticketIsCompleted;

  return (
    <>
      <TicketTaskEditModal
        show={showEditModal}
        handleClose={() => setShowEditModal(false)}
        ticketId={ticketId}
        data={task}
        agents={agents}
        refreshTasks={refresh}
      />
      <DeadlineTaskEditModal
        show={showEditDeadlineModal}
        close={() => setShowEditDeadlineModal(false)}
        taskData={taskToDeadlineEdit}
        setTaskData={setTaskDeadlineEdit}
      />
      <DeadlineTaskEditModal
        show={showEditDeadlineBulkActionModal}
        close={() => setShowEditDeadlineBulkActionModal(false)}
        taskData={bulkBody}
        setTaskData={setBulkBody}
      />
      <Flex
        justifyContent={'between'}
        alignItems={'center'}
        className="px-3 mb-2 bulk-actions"
      >
        <Flex>
          <Button
            variant="link"
            size="sm"
            role="none"
            style={{ paddingRight: '10px' }}
          >
            <span className="d-none">draggable</span>
          </Button>
          <Form.Check
            type="checkbox"
            disabled={ticketIsCompleted}
            checked={selectedTasks.length === taskItems.length}
            ind
            className="me-3"
            onClick={() => selectAllTasks()}
            ref={selectAllCheckboxRef}
            label={`Selecionar todas (${
              // Count tasks excluding group titles
              tasks.filter(task => !task.isGroupTitle).length
            })`}
          />
        </Flex>

        {selectedTasks.length > 0 && (
          <Flex alignItems="center" className="gap-2">
            <SingleSelect
              options={optionsBulkActions}
              placeholder="Ações em massa"
              size="sm"
              isDisabled={ticketIsCompleted}
              defaultValue={optionsBulkActions[0]}
              value={selectedBulkAction}
              onChange={setSelectedBulkAction}
            />

            {bulkBody.operation === 'SET_DEADLINE' && (
              <>
                <span className="text-nowrap fs-9 text-secondary">
                  {formatDeadlineSpecificTime(bulkBody.deadline)}
                </span>
              </>
            )}

            {bulkBody.operation === 'SET_AGENT' && (
              <SingleSelect
                options={formattedAgents}
                placeholder="Agentes"
                size="sm"
                isDisabled={ticketIsCompleted}
                value={selectedAgentBulkAction}
                onChange={setSelectedAgentBulkAction}
              />
            )}

            <Button
              size="sm"
              disabled={isBulkActionDisabled}
              onClick={() => applyBulkAction()}
            >
              Aplicar
            </Button>
          </Flex>
        )}
      </Flex>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="tasks-list">
          {provided => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {taskItems.map((task, index) => {
                const isEditing = editingField.taskId === task.id;
                const currentField = isEditing ? editingField.field : null;
                const taskEditData = editData[task.id] || {
                  title: task.title,
                  deadline: task.deadline
                    ? task.deadline.substr(0, task.deadline.lastIndexOf(':'))
                    : '',
                  agent: { id: task.agent?.id || '' }
                };

                return (
                  <Draggable
                    key={task.id.toString()}
                    draggableId={task.id.toString()}
                    index={index}
                    isDragDisabled={ticketIsCompleted}
                  >
                    {(provided, snapshot) => (
                      <div ref={provided.innerRef} {...provided.draggableProps}>
                        <DialogModal
                          showModal={
                            showDeleteConfirmModal && taskIdToDelete === task.id
                          }
                          handleClose={() => setShowDeleteConfirmModal(false)}
                          message={'Tem certeza que deseja excluir a tarefa?'}
                          response={handleDeleteTask}
                        />
                        <Card
                          role="button"
                          className={`task border-0 ${
                            snapshot.isDragging ? 'dragging' : ''
                          }`}
                          style={{
                            borderRadius: '8px',
                            marginBottom: '3px',
                            opacity: localTaskStatus[task.id] ? 0.5 : 1,
                            ...(snapshot.isDragging
                              ? {
                                  boxShadow: '0 5px 10px rgba(0,0,0,0.3)',
                                  backgroundColor: '#f8f9fa'
                                }
                              : {})
                          }}
                          ref={isEditing ? editingCardRef : null}
                        >
                          <Card.Body
                            as={Flex}
                            justifyContent="between"
                            alignItems="center"
                            className="py-2 px-3"
                            style={{ minHeight: '64px' }}
                          >
                            <Flex alignItems="center" style={{ flex: 1 }}>
                              <div
                                {...provided.dragHandleProps}
                                className="p-0 text-400 me-3"
                                title="Arraste para reorganizar"
                                style={{
                                  cursor: ticketIsCompleted
                                    ? 'not-allowed'
                                    : 'grab',
                                  color: '#6c757d'
                                }}
                              >
                                <FontAwesomeIcon
                                  icon={faGripVertical}
                                  size="sm"
                                />
                              </div>

                              {/* Checkbox para seleção */}
                              <Form.Check
                                type="checkbox"
                                className="me-3"
                                disabled={ticketIsCompleted}
                                value={task.id}
                                onChange={handleSelectTask}
                                checked={selectedTasks.includes(task.id)}
                                style={{
                                  cursor: ticketIsCompleted
                                    ? 'not-allowed'
                                    : 'pointer'
                                }}
                              />

                              {/* Botão de conclusão */}
                              {!task.isGroupTitle && (
                                <Button
                                  variant="link"
                                  size="sm"
                                  disabled={ticketIsCompleted}
                                  className={`me-2 ${
                                    localTaskStatus[task.id]
                                      ? 'bg-success border-0 text-white'
                                      : ''
                                  }`}
                                  onClick={() =>
                                    handleStatusChange(
                                      task.id,
                                      localTaskStatus[task.id]
                                    )
                                  }
                                  style={{
                                    width: '24px',
                                    height: '24px',
                                    padding: '0',
                                    color: '#e0e0e0',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: ticketIsCompleted
                                      ? 'not-allowed'
                                      : 'pointer'
                                  }}
                                >
                                  <FontAwesomeIcon
                                    icon={faCheck}
                                    className="fs-11"
                                  />
                                </Button>
                              )}

                              {/* Título da tarefa - editável ao clicar */}
                              {currentField === 'title' ? (
                                <Form.Control
                                  ref={titleInputRef}
                                  value={taskEditData.title}
                                  onChange={e =>
                                    handleInputChange(
                                      task.id,
                                      'title',
                                      e.target.value
                                    )
                                  }
                                  onKeyDown={e => handleKeyDown(e, task.id)}
                                  onBlur={() => {
                                    saveChanges(task.id);
                                    setEditingField({
                                      taskId: null,
                                      field: null
                                    });
                                  }}
                                  size={task.isGroupTitle ? 'lg' : 'sm'}
                                  className="border-0 shadow-none"
                                  style={{ maxWidth: '60%' }}
                                />
                              ) : (
                                <Button
                                  variant="link"
                                  className={`task-title p-0 ${
                                    task.isGroupTitle
                                      ? 'fw-semibold fs-8 text-body-emphasis'
                                      : ''
                                  }`}
                                  disabled={ticketIsCompleted}
                                  style={{
                                    textDecoration: localTaskStatus[task.id]
                                      ? 'line-through'
                                      : 'none',
                                    color: localTaskStatus[task.id]
                                      ? '#6c757d'
                                      : 'inherit'
                                  }}
                                  onClick={() => startEditing(task, 'title')}
                                >
                                  {task.title}
                                </Button>
                              )}
                            </Flex>

                            {!task.isGroupTitle ? (
                              <Flex
                                alignItems="center"
                                className="task-actions"
                              >
                                <Button
                                  variant="link"
                                  className={`mx-3 p-0 fs-10 text-decoration-none ${getDeadlineColor(
                                    task.deadline
                                  )}`}
                                  disabled={ticketIsCompleted}
                                  onClick={() => {
                                    setTaskDeadlineEdit(task);
                                    setShowEditDeadlineModal(true);
                                  }}
                                >
                                  {formatDeadlineSpecificTime(
                                    task.deadline
                                  ) || <FontAwesomeIcon icon={faCalendar} />}
                                </Button>

                                {/* Tooltip para descrição */}
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip
                                      id={`tooltip-description-${index}`}
                                    >
                                      {task.description
                                        ? task.description
                                        : 'Descrição da tarefa'}
                                    </Tooltip>
                                  }
                                >
                                  <Button
                                    variant="link"
                                    className="mx-3 p-0 text-secondary"
                                    disabled={ticketIsCompleted}
                                    onClick={() => {
                                      setTask(task);
                                      setShowEditModal(true);
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faAlignJustify} />
                                  </Button>
                                </OverlayTrigger>

                                {/* Avatar/seleção de agente */}
                                {currentField === 'agent' ? (
                                  // <Form.Select
                                  //   ref={agentSelectRef}
                                  //   size="sm"
                                  //   value={taskEditData.agent.id}
                                  //   onChange={e =>
                                  //     handleInputChange(task.id, 'agent', {
                                  //       id: parseInt(e.target.value)
                                  //     })
                                  //   }
                                  //   onBlur={() => saveChanges(task.id)}
                                  //   style={{ width: '150px' }}
                                  //   className="mx-3"
                                  // >
                                  //   <option value="">
                                  //     Selecione um agente
                                  //   </option>
                                  //   {agents?.map(agent => (
                                  //     <option key={agent.id} value={agent.id}>
                                  //       {agent.firstName} {agent.lastName}
                                  //     </option>
                                  //   ))}
                                  // </Form.Select>

                                  <SingleSelect
                                    ref={agentSelectRef}
                                    options={formattedAgents}
                                    placeholder="Agentes"
                                    size="sm"
                                    isDisabled={ticketIsCompleted}
                                    value={selectedAgent}
                                    onChange={e => handleChangeAgent(e, task)}
                                    onMenuClose={() => {
                                      if (
                                        editingField.taskId === task.id &&
                                        editingField.field === 'agent'
                                      ) {
                                        setEditingField({
                                          taskId: null,
                                          field: null
                                        });
                                        setSelectedAgent(null);
                                      }
                                    }}
                                  />
                                ) : task.agent ? (
                                  <Button
                                    variant="link"
                                    className="p-0 text-secondary"
                                    disabled={ticketIsCompleted}
                                    onClick={() => startEditing(task, 'agent')}
                                  >
                                    <AvatarTask
                                      src={getAgentPhoto(task.agent.id)}
                                      name={
                                        `${task.agent.firstName} ${task.agent.lastName}` ||
                                        '?'
                                      }
                                      className="mx-3"
                                    />
                                  </Button>
                                ) : (
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    className="mx-3 rounded-circle"
                                    disabled={ticketIsCompleted}
                                    style={{
                                      width: '30px',
                                      height: '30px',
                                      padding: 0,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                    onClick={() => startEditing(task, 'agent')}
                                  >
                                    <FontAwesomeIcon
                                      icon="user-plus"
                                      className="fs-11"
                                    />
                                  </Button>
                                )}

                                {/* Botão de contexto com dropdown */}
                                <Dropdown
                                  className="btn-reveal-trigger opacity-100"
                                  align="end"
                                >
                                  <Dropdown.Toggle
                                    as={Button}
                                    variant="link"
                                    className="p-0 text-secondary ms-2"
                                    disabled={ticketIsCompleted}
                                  >
                                    <FontAwesomeIcon icon="ellipsis-h" />
                                  </Dropdown.Toggle>

                                  <Dropdown.Menu>
                                    <Dropdown.Item
                                      style={{ cursor: 'default' }}
                                      className="fs-11 p-0 text-secondary"
                                    >
                                      Transformar em...
                                    </Dropdown.Item>
                                    <Dropdown.Divider />
                                    <Dropdown.Item
                                      disabled={!task.isGroupTitle}
                                      onClick={() => convertToTask(task)}
                                    >
                                      Tarefa
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      disabled={task.isGroupTitle}
                                      onClick={() => convertToTitle(task)}
                                    >
                                      Título
                                    </Dropdown.Item>
                                    <Dropdown.Divider />
                                    <Dropdown.Item
                                      onClick={() => {
                                        setTask(task);
                                        setShowEditModal(true);
                                      }}
                                    >
                                      Editar
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      className="text-danger"
                                      onClick={() => {
                                        setTaskIdToDelete(task.id);
                                        setShowDeleteConfirmModal(true);
                                      }}
                                    >
                                      Excluir
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </Flex>
                            ) : (
                              <Dropdown
                                className="btn-reveal-trigger opacity-100"
                                align="end"
                              >
                                <Dropdown.Toggle
                                  as={Button}
                                  variant="link"
                                  className="p-0 text-secondary ms-2"
                                  disabled={ticketIsCompleted}
                                >
                                  <FontAwesomeIcon icon="ellipsis-h" />
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                  <Dropdown.Item
                                    style={{ cursor: 'default' }}
                                    className="fs-11 p-0 text-secondary"
                                  >
                                    Transformar em...
                                  </Dropdown.Item>
                                  <Dropdown.Divider />
                                  <Dropdown.Item
                                    disabled={!task.isGroupTitle}
                                    onClick={() => convertToTask(task)}
                                  >
                                    Tarefa
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    disabled={task.isGroupTitle}
                                    onClick={() => convertToTitle(task)}
                                  >
                                    Título
                                  </Dropdown.Item>
                                  <Dropdown.Divider />
                                  <Dropdown.Item
                                    className="text-danger"
                                    onClick={() => {
                                      setTaskIdToDelete(task.id);
                                      setShowDeleteConfirmModal(true);
                                    }}
                                  >
                                    Excluir
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            )}
                          </Card.Body>
                        </Card>
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </>
  );
};

export default TaskCard;
